import { ShouldRevalidateFunction, useRouteError } from "@remix-run/react";
import { UNSAFE_ErrorResponseImpl } from "@remix-run/router";
import { ComponentType, lazy } from "react";
import { getRootLoaderDataFromWindow, useRootLoaderData } from "~/utils";

export const mergeComponents = ({
  landing,
  user,
  userApp,
}: {
  landing?: () => Promise<{
    default: ComponentType;
    // do not export server code from the same file
    loader?: never;
    action?: never;
    // this should be inlined directly in mergeShouldRevalidate
    shouldRevalidate?: never;
  }>;
  user?: () => Promise<{
    default: ComponentType;
    // do not export server code from the same file
    loader?: never;
    action?: never;
    // this should be inlined directly in mergeShouldRevalidate
    shouldRevalidate?: never;
  }>;
  userApp?: () => Promise<{
    default: ComponentType;
    // do not export server code from the same file
    loader?: never;
    action?: never;
    // this should be inlined directly in mergeShouldRevalidate
    shouldRevalidate?: never;
  }>;
}) => {
  const LazyLanding = landing && lazy(() => landing());
  const LazyUser = user && lazy(() => user());
  const LazyUserApp = userApp && lazy(() => userApp());

  function MergedComponent() {
    const rootLoaderData = useRootLoaderData();

    switch (true) {
      case rootLoaderData.requestType === "Landing" && !!LazyLanding:
        return <LazyLanding />;
      case rootLoaderData.requestType === "User" && !!LazyUser:
        return <LazyUser />;
      case rootLoaderData.requestType === "UserApp" && !!LazyUserApp:
        return <LazyUserApp />;
      case rootLoaderData.requestType === "Revision" && !!LazyUserApp:
        return <LazyUserApp />;
      default:
        throw new UNSAFE_ErrorResponseImpl(404, "Not Found", undefined, true);
    }
  }

  return MergedComponent;
};

export const mergeErrorBoundaries = ({
  landing,
  user,
  userApp,
}: {
  landing?: () => Promise<{ ErrorBoundary: ComponentType }>;
  user?: () => Promise<{ ErrorBoundary: ComponentType }>;
  userApp?: () => Promise<{ ErrorBoundary: ComponentType }>;
}) => {
  const LazyLanding = landing && lazy(() => landing().then((m) => ({ default: m.ErrorBoundary })));
  const LazyUser = user && lazy(() => user().then((m) => ({ default: m.ErrorBoundary })));
  const LazyUserApp = userApp && lazy(() => userApp().then((m) => ({ default: m.ErrorBoundary })));

  function MergedErrorBoundary() {
    const rootLoaderData = useRootLoaderData();
    const error = useRouteError();

    switch (true) {
      case rootLoaderData.requestType === "Landing" && !!LazyLanding:
        return <LazyLanding />;
      case rootLoaderData.requestType === "User" && !!LazyUser:
        return <LazyUser />;
      case rootLoaderData.requestType === "UserApp" && !!LazyUserApp:
        return <LazyUserApp />;
      case rootLoaderData.requestType === "Revision" && !!LazyUserApp:
        return <LazyUserApp />;
      default:
        throw error;
    }
  }

  return MergedErrorBoundary;
};

export const mergeShouldRevalidate =
  ({
    landing,
    user,
    userApp,
  }: {
    landing?: ShouldRevalidateFunction;
    user?: ShouldRevalidateFunction;
    userApp?: ShouldRevalidateFunction;
  }): ShouldRevalidateFunction =>
  (args) => {
    const rootLoaderData = getRootLoaderDataFromWindow();

    switch (true) {
      case rootLoaderData.data?.requestType === "Landing" && !!landing:
        return landing(args);
      case rootLoaderData.data?.requestType === "User" && !!user:
        return user(args);
      case rootLoaderData.data?.requestType === "UserApp" && !!userApp:
        return userApp(args);
      case rootLoaderData.data?.requestType === "Revision" && !!userApp:
        return userApp(args);
      default:
        return args.defaultShouldRevalidate;
    }
  };
